/**
 * Hosts popped-out threads at the app shell so they stay open and live while
 * the main window navigates. Each popout is a child window rendered by this
 * renderer (see {@link PopoutWindow}); docking returns it to its channel or
 * conversation as `?thread=`.
 */

import { type ReactNode, Suspense, lazy, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PopoutWindow } from '../../components/popout/PopoutWindow';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { usePlatformFeatures } from '../../config/PlatformContext';
import { ThreadPopoutsContext, type ThreadPopoutTarget, type ThreadPopoutsValue } from './threadPopoutContext';

const SpaceThreadContainer = lazy(() =>
  import('../spaces/SpaceThreadContainer').then((m) => ({ default: m.SpaceThreadContainer })),
);
const ConversationThreadContainer = lazy(() =>
  import('../conversations/ConversationThreadContainer').then((m) => ({ default: m.ConversationThreadContainer })),
);

function dockPath(target: ThreadPopoutTarget): string {
  const thread = `?thread=${encodeURIComponent(target.rootId)}`;
  if (target.kind === 'space') {
    return `/s/${encodeURIComponent(target.space.slug)}/c/${encodeURIComponent(target.space.channelId)}${thread}`;
  }
  return `/conversations/${encodeURIComponent(target.conversation.conversationId)}${thread}`;
}

export function ThreadPopoutProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const supported = usePlatformFeatures().hasThreadPopout === true;
  const [popouts, setPopouts] = useState<ThreadPopoutTarget[]>([]);
  const windowsRef = useRef(new Map<string, Window>());

  const remove = useCallback((rootId: string) => {
    windowsRef.current.delete(rootId);
    setPopouts((prev) => prev.filter((p) => p.rootId !== rootId));
  }, []);

  const popOut = useCallback(
    (target: ThreadPopoutTarget) => {
      if (!supported) return;
      const existing = windowsRef.current.get(target.rootId);
      if (existing && !existing.closed) {
        existing.focus();
        return;
      }
      setPopouts((prev) => (prev.some((p) => p.rootId === target.rootId) ? prev : [...prev, target]));
    },
    [supported],
  );

  const dock = useCallback(
    (target: ThreadPopoutTarget) => {
      navigate(dockPath(target));
      remove(target.rootId);
      window.focus();
    },
    [navigate, remove],
  );

  const value = useMemo<ThreadPopoutsValue>(
    () => ({
      supported,
      popOut,
      isPoppedOut: (rootId) => popouts.some((p) => p.rootId === rootId),
    }),
    [supported, popOut, popouts],
  );

  return (
    <ThreadPopoutsContext.Provider value={value}>
      {children}
      {popouts.map((target) => {
        const close = () => remove(target.rootId);
        const label =
          target.kind === 'space'
            ? t('threads.channelTarget', { channel: target.space.channelName })
            : target.conversation.title;
        return (
          <PopoutWindow
            key={target.rootId}
            id={target.rootId}
            title={`${t('threads.panelTitle')} · ${label}`}
            onClosed={close}
            onBlocked={() => toast.error(t('threads.popoutBlocked'))}
            onOpened={(win) => windowsRef.current.set(target.rootId, win)}
          >
            <Suspense fallback={<Spinner size="md" />}>
              {target.kind === 'space' ? (
                <SpaceThreadContainer
                  target={target.space}
                  messageId={target.rootId}
                  variant="popout"
                  onClose={close}
                  onDock={() => dock(target)}
                />
              ) : (
                <ConversationThreadContainer
                  target={target.conversation}
                  messageId={target.rootId}
                  variant="popout"
                  onClose={close}
                  onDock={() => dock(target)}
                />
              )}
            </Suspense>
          </PopoutWindow>
        );
      })}
    </ThreadPopoutsContext.Provider>
  );
}
